/**
 * The user's consent choice, reduced to one word for tracking and for anything else on the page
 * that only needs to know whether consent was given.
 *
 * `@tv2/lib-consent` carries the value on the consent manager (`getConsentTrackingStatus()`), and
 * leaves it to each CMP provider to record it. This module is that judgement for the web, by the
 * same rule Smart TV applies (`deriveConsentTrackingStatus` in `@tv2/lib-consent/consentDetails`):
 * every group the CMP exposes must be granted.
 *
 * | Situation                                    | Status        |
 * | -------------------------------------------- | ------------- |
 * | No CMP configured                            | `notDecided` |
 * | Cookiebot active, nothing answered yet       | `notDecided` |
 * | Every category granted                       | `accepted`    |
 * | Answered, but not every category granted     | `declined`    |
 *
 * Alongside it the per-group record for tracking (`getConsentDetails()`) is kept in the
 * cross-platform format from `@tv2/lib-consent/consentDetails`: the three Cookiebot categories
 * as recorded, and the IAB groups - which Cookiebot does not expose - always `0`. While Cookiebot
 * is active but has no answer yet every group is `0`; with no CMP configured the record stays
 * `undefined` and tracking omits the key.
 *
 * The status is also written to `localStorage` under {@link CONSENT_STATUS_STORAGE_KEY}, as a value
 * of its own, so it can be read without going through the manager. It is write-only from here: the
 * manager is the source of truth, and the stored value is never read back into it.
 *
 * {@link connectConsentStatus} keeps all of it in step with every recorded decision.
 */
import {
  type ConsentTrackingStatus,
  getCurrentConsentDecision,
  isConsentIntegrationEnabled,
  setConsentDetails,
  setConsentTrackingStatus,
  subscribeConsentDecision,
  ALL_CONSENT_DENIED,
} from '@tv2/lib-consent/consentManager';
import {
  COOKIEBOT_EXPOSED_CATEGORIES,
  deriveConsentTrackingStatus,
  formatConsentDetails,
} from '@tv2/lib-consent/consentDetails';

export const CONSENT_STATUS_STORAGE_KEY = 'consentStatus';

/**
 * The status the recorded decision amounts to right now. Judged on the categories Cookiebot
 * exposes; the IAB groups it cannot grant are left out, exactly as consentDetails reports them 0.
 */
export function readConsentStatus(): ConsentTrackingStatus {
  if (!isConsentIntegrationEnabled()) return 'notDecided';
  return deriveConsentTrackingStatus(getCurrentConsentDecision(), COOKIEBOT_EXPOSED_CATEGORIES);
}

/**
 * The per-group tracking record the recorded decision amounts to right now, or `undefined` while
 * no CMP is configured - no CMP means no claim, and the key is omitted.
 */
export function readConsentDetails(): string | undefined {
  if (!isConsentIntegrationEnabled()) return undefined;
  return formatConsentDetails('cookiebot', getCurrentConsentDecision() ?? ALL_CONSENT_DENIED);
}

/** The status last written to storage, or `null` when nothing has been written yet. */
export function getPersistedConsentStatus(): ConsentTrackingStatus | null {
  try {
    return localStorage.getItem(CONSENT_STATUS_STORAGE_KEY) as ConsentTrackingStatus | null;
  } catch {
    return null;
  }
}

function persistConsentStatus(status: ConsentTrackingStatus): void {
  try {
    localStorage.setItem(CONSENT_STATUS_STORAGE_KEY, status);
  } catch {
    // Storage can be unavailable or full. The manager still has the value.
  }
}

function recordConsentStatus(): void {
  const status = readConsentStatus();
  setConsentTrackingStatus(status);
  setConsentDetails(readConsentDetails());
  persistConsentStatus(status);
}

/**
 * Starts keeping the manager's tracking status and details and the stored value in step with the
 * recorded consent. Returns a function that stops doing so.
 *
 * Called by the Cookiebot provider when it starts, since recording the status is the provider's
 * job. Records whatever is known right away, and again on every decision the manager is handed.
 */
export function connectConsentStatus(): () => void {
  recordConsentStatus();
  return subscribeConsentDecision(recordConsentStatus);
}
